/**
 * Phase 35 Session 35.2b-1 — `HelpTooltip` (DOM variant).
 *
 * The merchant-facing `?` icon that fetches its body from the
 * Help Center backend via `useTooltip(helpKey)` and renders the
 * rich Portable Text content in a popover. Composes with the
 * existing `@heroui/react` Tooltip primitive (Root + Trigger +
 * Content) so the visual vocabulary (focus ring, arrow, animations,
 * keyboard handling) matches every other tooltip in the dashboard
 * and web POS without forking.
 *
 * Architecture decision — lives in `@commerce-os/help-client`, not
 * in `@heroui/react`:
 *   - The component depends on `@tanstack/react-query` (for the
 *     hook fetch) and `@commerce-os/api-client` (for the typed
 *     wrapper). Adding those as hard deps to `@heroui/react`
 *     bloats every dashboard + storefront consumer that doesn't
 *     use the help system. Keeping `@heroui/react` pristine + the
 *     help-aware variant in help-client is the cleaner separation.
 *   - help-client takes `@heroui/react` as an OPTIONAL peer dep so
 *     RN consumers (rn-shared, where 35.2b-2 lands the RN variant)
 *     don't need to install it.
 *
 * Component API matches the Phase 35 spec:
 *   - `<HelpTooltip helpKey="products.sku"/>` — auto-renders a
 *     small `?` icon trigger + the help body in the popover.
 *   - `<HelpTooltip helpKey="..." iconSize="md"/>` — sizes the
 *     icon trigger.
 *   - `<HelpTooltip helpKey="..." trigger={<CustomIcon/>}/>` —
 *     replaces the default trigger with the consumer's element
 *     (still wraps in TooltipTrigger / FocusableSpan for a11y).
 *   - When `isUnavailable` (caller not entitled OR helpKey
 *     unknown), renders `null` — no DOM node, no aria-label, no
 *     `?` icon. Gating-via-invisibility, the Phase 35 contract.
 */
import {
  Tooltip as HeroTooltip,
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
} from '@heroui/react';
import type { ReactNode } from 'react';
import { useTooltip } from './hooks/useTooltip';
import { renderPortableText } from './render/portableText';

export interface HelpTooltipProps {
  /** The CMS-side helpKey. Required. Must be non-empty + match a
   *  registered helpKey in the CMS or the in-repo MDX fallback. */
  helpKey: string;
  /** Visual size of the default `?` icon. Defaults to 'sm'. */
  iconSize?: 'sm' | 'md' | 'lg';
  /** Override the default `?` icon trigger. The element is wrapped
   *  in HeroUI's TooltipTrigger so it receives the right ARIA + a
   *  focusable wrapper for keyboard users. */
  trigger?: ReactNode;
  /** Optional placement override; passed through to TooltipContent. */
  placement?: 'top' | 'right' | 'bottom' | 'left';
  /** When set, used in place of the automatic body rendering. Lets
   *  consumer surfaces opt into a custom rendering path (e.g. the
   *  dashboard help-center inline preview). */
  renderBody?: (body: string) => ReactNode;
  /** Optional className for the popover content. */
  className?: string;
  /** When false, skips the fetch entirely + renders nothing. */
  enabled?: boolean;
}

const ICON_SIZE_PX: Record<NonNullable<HelpTooltipProps['iconSize']>, number> =
  {
    sm: 14,
    md: 16,
    lg: 20,
  };

/**
 * Help-aware tooltip. Renders nothing when the caller isn't
 * entitled OR the helpKey isn't found — gating-via-invisibility,
 * the Phase 35 spec contract.
 */
export function HelpTooltip(props: HelpTooltipProps): ReactNode {
  const {
    helpKey,
    iconSize = 'sm',
    trigger,
    placement = 'top',
    renderBody,
    className,
    enabled = true,
  } = props;
  const { data, isLoading, isUnavailable } = useTooltip(helpKey, { enabled });

  // Hide entirely while loading the first response so the merchant
  // never sees a flickering empty popover. After resolution, hide
  // when unavailable (gating).
  if (!enabled) return null;
  if (isLoading) return null;
  if (isUnavailable || !data) return null;

  const triggerNode = trigger ?? <DefaultHelpIcon size={ICON_SIZE_PX[iconSize]} />;
  const body = renderBody ? renderBody(data.body) : renderPortableText(data.body);

  return (
    <TooltipRoot>
      <TooltipTrigger aria-label={`Help for ${helpKey}`} data-help-key={helpKey} tabIndex={0}>
        <span
          style={{ display: 'inline-flex', cursor: 'help' }}
        >
          {triggerNode}
        </span>
      </TooltipTrigger>
      <TooltipContent placement={placement} className={className} showArrow>
        <div style={{ maxWidth: 360, padding: 4 }}>{body}</div>
      </TooltipContent>
    </TooltipRoot>
  );
}

/**
 * Default `?` icon used when the consumer doesn't supply a
 * `trigger`. Inline SVG so the component has no icon-package dep.
 * Renders an outlined circle with a `?` glyph.
 */
function DefaultHelpIcon({ size }: { size: number }): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
      focusable="false"
      style={{
        verticalAlign: 'middle',
        flexShrink: 0,
        opacity: 0.65,
      }}
    >
      <circle cx="8" cy="8" r="6.5" />
      <path
        d="M6.25 6c0-.97.78-1.75 1.75-1.75S9.75 5.03 9.75 6c0 .65-.36.97-.75 1.25-.5.36-1 .73-1 1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="11" r=".5" fill="currentColor" />
    </svg>
  );
}

// Reserved namespace export — future polish + 35.2b-2 RN variant
// keep parity by re-exporting the help-aware compound members
// from a single name. Today only the basic HelpTooltip exists.
export { HelpTooltip as Tooltip };

// Re-export the underlying primitives so consumers can drop the
// help-aware variant + use the raw HeroUI Tooltip in the same
// import statement.
export {
  HeroTooltip as RawTooltip,
  TooltipContent as RawTooltipContent,
  TooltipRoot as RawTooltipRoot,
  TooltipTrigger as RawTooltipTrigger,
};
